import { CommonModule, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule, HttpErrorResponse, } from '@angular/common/http';
import localeEs from '@angular/common/locales/es';
import localePt from '@angular/common/locales/pt';
import { APP_INITIALIZER, ErrorHandler, Injectable, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';


import { IonicModule, IonicRouteStrategy, ToastController, } from '@ionic/angular';

import * as Sentry from '@sentry/browser';
import { AuthInterceptor, BackendService, BaseUrlService, } from '@tg4/http-infrastructure/dist/src';
import { QRCodeModule } from 'angularx-qrcode';
import { DragulaModule } from 'ng2-dragula';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';

import { AppComponent } from './app.component';
import { DiversityCheckModule } from './diversity-check/diversity-check.module';
import { GoalsModule } from './goals/goals.module';

import { LoginModule } from './login/login.module';
import { PerformanceCycleService } from './performance-cycles/performance-cycles.service';
import { BaseClass } from './shared/classes/base';
import { DirectivesModule } from './shared/directives/directives.module';
import { DiversityService } from './shared/diversity.service';
import { PipesModule } from './shared/pipes/pipes.module';
import { AuthService } from './shared/services/auth.service';
import { CycleService } from './shared/services/cycle.service';
import { ImpersonateInterceptor } from './shared/services/interceptor.service';
import { SharedService } from './shared/services/shared.service';
import { TranslationService } from './shared/services/translation.service';
import { UrlService } from './shared/services/url.service';
import { UsersModule } from './users/users.module';
import {CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import { LogoService } from './shared/services/logo.service';
import { NotificationService } from './shared/services/notification.service';
import { NotificationModalModule } from './shared/modals/notification-modal/notification-modal.module';
import { AiSummaryService } from './shared/services/ai-summary.service';
import { ExternalClientStylesService } from './external-client-styles/external-client-styles.service';
import { AttributesService } from './processes/attributes.service';
import { ConfigService } from './shared/services/multi-clients/config.service';
import { CdkDragPlaceholder } from "@angular/cdk/drag-drop";
import { BrandingService } from './shared/services/branding.service';

registerLocaleData(localePt, 'pt');
registerLocaleData(localeEs, 'es');

if (environment.production && !_isIE()) {
    Sentry.init({
        dsn: 'https://16df9f870ba74f6e8f93d0ca9905835f@sentry.io/1494353',
    });

    Sentry.configureScope((scope) => {
        const authData = localStorage.getItem('auth_data');
        if (authData) {
            const user = JSON.parse(authData);
            scope.setUser({
                id: user.user_id,
                username: user.username,
            });
        }
    });
}

function _isIE() {
    const userAgent = window.navigator.userAgent;
    return (
            userAgent.indexOf('MSIE ') > -1 || userAgent.indexOf('Trident/') > -1 // IE 10-
    ); // IE 11+
}

@Injectable()
export class SentryErrorHandler extends BaseClass implements ErrorHandler {
    constructor(
            protected _toastController: ToastController,
            protected _translationService: TranslationService
    ) {
        super(_toastController, _translationService);
    }

    handleError(error) {
        if (
                error instanceof HttpErrorResponse ||
                error.rejection instanceof HttpErrorResponse
        ) {
            // Server Error

            const errorObj =
                    error instanceof HttpErrorResponse
                            ? (error as HttpErrorResponse)
                            :(error.rejection as HttpErrorResponse);

            if (!navigator.onLine) {
                return this.notify(
                        this._translationService.getTranslation('error.noInternetConexion'),
                        'danger'
                );
            }

            // if (!(errorObj.status === 401)) {
            Sentry.captureException((error as any).originalError || error);
            // }
            return this.notifyError(error);
        } else {
            // Client Error
            Sentry.captureException((error as any).originalError || error);
            return this.notify('error.applicationError', 'danger');
        }
    }
}

/**
 * Inicializa a aplicação seguindo a ordem:
 * 1. Busca branding da API (baseado no hostname)
 * 2. Carrega configurações do env.json
 * 3. Faz merge de branding + env.json
 *
 * Os dados de branding incluem:
 * - clientName: Nome do cliente/parceiro
 * - logo: URL da logo customizada
 * - loginScreenImage: URL da imagem de fundo do login
 * - themeScss: CSS customizado inline
 * - favicon16x16, favicon32x32: URLs dos favicons
 *
 * Os serviços ThemeService e AssetService (chamados no AppComponent)
 * automaticamente usam esses dados quando disponíveis, com fallback
 * para recursos locais caso não existam.
 */
export function initializeBrandingAndConfig(
  brandingService: BrandingService,
  configService: ConfigService
) {
  return async () => {
    try {
      // 1. Carrega env.json primeiro
      await configService.loadConfig();

      // 2. Verifica se há email salvo no localStorage (prioridade)
      const lastEmail = brandingService.getLastEmail();
      let brandingApplied = false;

      if (lastEmail) {
        console.log('[APP_INITIALIZER] Last email found in localStorage, fetching fresh branding data');
        try {
          const emailBrandingResponse = await brandingService.checkEmailDomain(lastEmail).toPromise();

          if (emailBrandingResponse?.success && emailBrandingResponse?.data?.found && emailBrandingResponse.data.branding) {
            configService.setBrandingData(emailBrandingResponse.data.branding);
            console.log('[APP_INITIALIZER] Fresh branding data loaded from email domain and merged successfully');
            await configService.loadConfig();
            brandingApplied = true;
          } else {
            console.log('[APP_INITIALIZER] No branding found for email domain, will try hostname');
          }
        } catch (emailError) {
          console.error('[APP_INITIALIZER] Error fetching branding by email, will try hostname:', emailError);
        }
      }

      // 3. Se não aplicou branding por email, busca por hostname
      if (!brandingApplied) {
        const hostname = window.location.hostname;
        const brandingResponse = await brandingService.getBrandingByHostname(hostname).toPromise();

        if (brandingResponse?.success && brandingResponse?.data?.found && brandingResponse.data.branding) {
          configService.setBrandingData(brandingResponse.data.branding);
          console.log('[APP_INITIALIZER] Branding data loaded from hostname and merged successfully');
          await configService.loadConfig();
        } else {
          console.log('[APP_INITIALIZER] No branding found for hostname, using default configuration');
        }
      }
    } catch (error) {
      console.error('[APP_INITIALIZER] Error during initialization:', error);
      // Não bloqueia inicialização - usa fallback do env.json
    }
  };
}

@NgModule({
    declarations: [AppComponent],
    imports: [
    BrowserAnimationsModule,
    CommonModule,
    BrowserAnimationsModule,
    IonicModule.forRoot(),
    HttpClientModule,
    LoginModule,
    GoalsModule,
    AppRoutingModule,
    UsersModule,
    PipesModule,
    DirectivesModule,
    DiversityCheckModule,
    QRCodeModule,
    DragulaModule.forRoot(),
    NotificationModalModule,
    CdkDragPlaceholder,
], providers: [
        { provide: LOCALE_ID, useValue: 'pt' },
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeBrandingAndConfig,
            deps: [BrandingService, ConfigService],
            multi: true
        },
        AuthService,
        CookieService,
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ImpersonateInterceptor,
            multi: true,
        },
        // { provide: ErrorHandler, useClass: SentryErrorHandler },
        BackendService,
        SharedService,
        CycleService,
        { provide: BaseUrlService, useClass: UrlService },
        TranslationService,
        LogoService,
        DiversityService,
        PerformanceCycleService,
        ExternalClientStylesService,
        NotificationService,
        AiSummaryService,
        AttributesService
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent]
})
export class AppModule {
}
